import { useCallback, useEffect, useMemo, useState } from 'react';
import { TRACKS } from '../constants/appConstants.js';
import { getRoadmapRoles, saveRoadmapRoles } from '../services/supabaseClient.js';

const MAX_ROADMAP_TOTAL = 9;
const MAX_ROADMAP_PER_TRACK = 3;

// A pinned roadmap role is identified by the specific Adzuna posting id, NOT by
// title — many live listings share a title (e.g. several "IT Support Specialist"
// from different companies), so a title match would flip every same-titled card
// to "Added ✓" on a single click. Falls back to (title, track_id) for legacy
// entries pinned before `id` was stored.
function roleMatchesJob(r, job, trackId) {
  return r.id != null ? r.id === job.id : (r.title === job.title && r.track_id === trackId);
}

// ─────────────────────────────────────────────────────────────────
// CareerHub — full-bleed Career Hub view (sidebar → "Career Hub").
//
// The single home for Adzuna job data. Layout:
//   LEFT (~65%):  Search filters → Find Matching Roles → Recommended
//                 Roles (real Adzuna listings, match-scored) → Career
//                 Path Map (authored from the track's job_roles ladder)
//   RIGHT (~35%): Career Goal · Job Match · Market Signal · Salary
//                 Snapshot — stacked stat cards
//
// AdaptOS design system: white elevated cards, amber CTAs, emerald
// match/positive signals, slate metadata. No teal CTAs.
// ─────────────────────────────────────────────────────────────────

const ADZUNA_QUERIES = {
  comptia_aplus:    'help desk',
  comptia_network:  'network administrator',
  comptia_security: 'security analyst',
  az_900:           'cloud administrator',
  az_104:           'azure administrator',
  ab_900:           'office 365 administrator',
};

const WORK_TYPES = [
  { id: 'all',    label: 'All' },
  { id: 'remote', label: 'Remote' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'onsite', label: 'On-site' },
];

const SALARY_BANDS = [
  { id: '0',     label: 'Any salary' },
  { id: '40000', label: '$40k+' },
  { id: '60000', label: '$60k+' },
  { id: '80000', label: '$80k+' },
];

// Stable string hash → deterministic match spread (no flicker on re-render).
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

// Heuristic match score over a REAL listing: title relevance to the track's
// target roles + a stable per-listing spread. Deterministic, 62–97.
function matchScore(job, track) {
  const title = (job.title || '').toLowerCase();
  const roleWords = new Set((track?.job_roles || []).join(' ').toLowerCase().split(/\s+/).filter(w => w.length > 3));
  const overlap = title.split(/\s+/).filter(w => roleWords.has(w)).length;
  const base = 72 + Math.min(18, overlap * 7);
  const spread = hashStr(job.id || job.title || '') % 8;
  return Math.max(62, Math.min(97, base + spread));
}

function salaryLabel(min, max) {
  if (!min && !max) return null;
  const k = (n) => `$${Math.round(n / 1000)}k`;
  if (min && max) return `${k(min)}–${k(max)}`;
  return `${k(min || max)}+`;
}

// ── Right-column stat card ─────────────────────────────────────
function StatCard({ icon, label, value, desc, cta }) {
  return (
    <div style={{ ...cardWhite, padding: '15px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--brand-amber)', background: 'rgba(217,119,6,0.12)',
        }}>
          {icon}
        </span>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--brand-slate)', margin: 0 }}>
          {label}
        </p>
      </div>
      <p style={{ fontSize: 18, fontWeight: 700, color: '#1e1e2e', margin: '9px 0 0', lineHeight: 1.25 }}>{value}</p>
      {desc && <p style={{ fontSize: 12, color: 'var(--brand-slate)', margin: '4px 0 0', lineHeight: 1.45 }}>{desc}</p>}
      {cta && (
        <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--brand-amber)', margin: '12px 0 0' }}>{cta} →</p>
      )}
    </div>
  );
}

// ── Recommended-role listing card ──────────────────────────────
function RoleCard({ job, track, inRoadmap, canAdd, onToggleRoadmap }) {
  const score  = matchScore(job, track);
  const salary = salaryLabel(job.salary_min, job.salary_max);
  const open   = () => window.open(job.redirect_url, '_blank', 'noopener');
  const [saved, setSaved] = useState(false);
  const roadmapDisabled = !inRoadmap && !canAdd;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '15px 17px', boxShadow: 'var(--card-shadow)',
      display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap',
    }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1e1e2e' }}>{job.title}</span>
          <span style={{
            fontSize: 11, fontWeight: 800, color: 'var(--brand-emerald)',
            background: 'rgba(5,150,105,0.12)', borderRadius: 99, padding: '2px 9px', whiteSpace: 'nowrap',
          }}>
            {score}% Match
          </span>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--brand-slate)', margin: '5px 0 0' }}>
          {job.company?.display_name || 'Company undisclosed'} · {job.location?.display_name || '—'}
        </p>
        {salary && (
          <p style={{ fontSize: 12.5, color: 'var(--brand-emerald)', fontWeight: 700, margin: '5px 0 0' }}>{salary}/yr</p>
        )}
      </div>
      <div className="ch-role-btns" style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        <button type="button" className="ch-role-btn" onClick={() => setSaved(s => !s)} style={btnSave}>{saved ? 'Saved ✓' : 'Save'}</button>
        <button type="button" className="ch-role-btn" onClick={() => !roadmapDisabled && onToggleRoadmap(job)} disabled={roadmapDisabled}
          style={{ ...btnRoadmap, opacity: roadmapDisabled ? 0.5 : 1, cursor: roadmapDisabled ? 'default' : 'pointer' }}>
          {inRoadmap ? 'Added ✓' : roadmapDisabled ? 'Roadmap full' : 'Add to Roadmap'}
        </button>
        <button type="button" className="ch-role-btn" onClick={open} style={btnApply}>Apply</button>
      </div>
    </div>
  );
}

// ── Career Path Map — authored from the track's job_roles ladder ─
function CareerPathMap({ track, compact }) {
  const roles = (track?.job_roles || []).slice(0, compact ? 4 : 5);
  if (roles.length === 0) return null;
  const stageLabel = (i) => (i === 0 ? 'Entry' : i === roles.length - 1 ? 'Senior' : 'Mid');
  const stageColor = (i) => (i === 0 ? 'var(--brand-amber)' : i === roles.length - 1 ? 'var(--brand-emerald)' : 'var(--brand-slate)');
  const cw = compact ? 128 : 168;
  const mh = compact ? 58 : 92;
  return (
    <div style={{ ...cardWhite, padding: compact ? '12px 14px' : '18px 20px', marginTop: compact ? 0 : 20 }}>
      <p style={{ ...sectionTitle, fontSize: compact ? 13 : 15 }}>Career Path Map</p>
      {!compact && (
        <p style={{ fontSize: 12.5, color: 'var(--brand-slate)', margin: '4px 0 16px' }}>
          A typical progression from your {track?.label} credential.
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, overflowX: 'auto', paddingBottom: 4, marginTop: compact ? 10 : 0 }}>
        {roles.map((role, i) => (
          <div key={role + i} style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
            <div style={{
              width: cw, minHeight: mh, flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: compact ? 10 : 12,
              padding: compact ? '8px 10px' : '12px 14px', background: i === 0 ? '#fffaf0' : '#fff',
              display: 'flex', flexDirection: 'column', gap: compact ? 3 : 6,
            }}>
              <span style={{ fontSize: compact ? 8.5 : 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: stageColor(i) }}>
                {stageLabel(i)}
              </span>
              <span style={{ fontSize: compact ? 11 : 13, fontWeight: 700, color: '#1e1e2e', lineHeight: 1.25 }}>{role}</span>
            </div>
            {i < roles.length - 1 && (
              <span style={{ flexShrink: 0, padding: compact ? '0 5px' : '0 8px', color: '#cbd2dc', fontSize: compact ? 13 : 18, fontWeight: 700 }} aria-hidden>→</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CareerHub({ learner, onNavigate }) {
  const [trackId,   setTrackId]   = useState(learner?.current_track || 'comptia_aplus');
  const track = TRACKS[trackId] || TRACKS.comptia_aplus;

  const [keyword,   setKeyword]   = useState('');
  const [location,  setLocation]  = useState('');
  const [salaryMin, setSalaryMin] = useState('0');
  const [workType,  setWorkType]  = useState('all');

  const [jobs,    setJobs]    = useState([]);
  const [count,   setCount]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(false);
  const [roadmapRoles, setRoadmapRoles] = useState([]);

  // Load the learner's pinned roadmap roles (drives Added ✓ + limits).
  useEffect(() => {
    if (!learner?.id) return undefined;
    let cancelled = false;
    getRoadmapRoles(learner.id).then(r => { if (!cancelled) setRoadmapRoles(r); });
    return () => { cancelled = true; };
  }, [learner?.id]);

  const toggleRoadmap = (job) => {
    const has = roadmapRoles.some(r => roleMatchesJob(r, job, trackId));
    let next;
    if (has) {
      next = roadmapRoles.filter(r => !roleMatchesJob(r, job, trackId));
    } else {
      const perTrack = roadmapRoles.filter(r => r.track_id === trackId).length;
      if (roadmapRoles.length >= MAX_ROADMAP_TOTAL || perTrack >= MAX_ROADMAP_PER_TRACK) return;
      next = [...roadmapRoles, { id: job.id, title: job.title, track_id: trackId, salary: salaryLabel(job.salary_min, job.salary_max) }];
    }
    setRoadmapRoles(next);
    saveRoadmapRoles(learner.id, next);
  };
  const perTrackCount = roadmapRoles.filter(r => r.track_id === trackId).length;
  const canAddRoadmap = roadmapRoles.length < MAX_ROADMAP_TOTAL && perTrackCount < MAX_ROADMAP_PER_TRACK;

  const fetchJobs = useCallback(async (tid, kw, loc) => {
    const appId  = import.meta.env.VITE_ADZUNA_APP_ID;
    const appKey = import.meta.env.VITE_ADZUNA_API_KEY;
    if (!appId || !appKey) { setError(true); return; }
    setLoading(true); setError(false); setJobs([]);
    try {
      const what  = (kw && kw.trim()) || ADZUNA_QUERIES[tid] || 'IT support';
      const where = loc && loc.trim() ? `&where=${encodeURIComponent(loc.trim())}` : '';
      const url = `https://api.adzuna.com/v1/api/jobs/us/search/1?app_id=${appId}&app_key=${appKey}&what=${encodeURIComponent(what)}${where}&category=it-jobs&results_per_page=25&content-type=application/json`;
      const res  = await fetch(url);
      const data = await res.json();
      if (data.exception) { setError(true); }
      else { setJobs(data.results || []); setCount(typeof data.count === 'number' ? data.count : null); }
    } catch { setError(true); }
    finally { setLoading(false); }
  }, []);

  // Auto-load on track change (and first mount).
  useEffect(() => { fetchJobs(trackId, '', location); /* eslint-disable-next-line */ }, [trackId]);

  // Client-side filters over the fetched listings.
  const filtered = useMemo(() => {
    const min = Number(salaryMin) || 0;
    return jobs.filter(j => {
      if (min && !(j.salary_max >= min || j.salary_min >= min)) return false;
      if (workType !== 'all') {
        const hay = `${j.title || ''} ${j.location?.display_name || ''} ${j.description || ''}`.toLowerCase();
        if (workType === 'remote' && !hay.includes('remote')) return false;
        if (workType === 'hybrid' && !hay.includes('hybrid')) return false;
        if (workType === 'onsite' && (hay.includes('remote') || hay.includes('hybrid'))) return false;
      }
      return true;
    });
  }, [jobs, salaryMin, workType]);

  const ranked = useMemo(
    () => filtered.map(j => ({ job: j, score: matchScore(j, track) })).sort((a, b) => b.score - a.score),
    [filtered, track],
  );

  // Derived stat values.
  const careerGoal = learner?.career_goal || track?.job_roles?.[0] || 'IT Professional';
  const topMatch   = ranked[0]?.score ?? (80 + (hashStr(trackId) % 12));
  const demand     = count == null ? null : count > 2500 ? 'Strong demand' : count > 600 ? 'Healthy demand' : 'Selective market';
  const salaryStat = useMemo(() => {
    const withSal = jobs.filter(j => j.salary_min && j.salary_max);
    if (withSal.length === 0) return null;
    const avgMin = withSal.reduce((s, j) => s + j.salary_min, 0) / withSal.length;
    const avgMax = withSal.reduce((s, j) => s + j.salary_max, 0) / withSal.length;
    return salaryLabel(avgMin, avgMax);
  }, [jobs]);

  return (
    <div style={{ maxWidth: 1180, zoom: 1.2 }}>
      {/* On narrow screens the role-card action buttons (Save / Add to
          Roadmap / Apply) overflowed the card because their row refused to
          shrink. Drop the row to full width and let the buttons share/wrap
          inside the card; trim their size so all three fit. !important
          overrides the inline layout styles on these elements. */}
      <style>{`
        @media (max-width: 600px) {
          .ch-role-btns { width: 100% !important; flex-basis: 100% !important; }
          .ch-role-btn {
            flex: 1 1 auto !important;
            padding: 8px 10px !important;
            font-size: 12px !important;
          }
        }
      `}</style>
      {/* Header — title left, compact Career Path Map right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 18, marginBottom: 20 }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1e1e2e', margin: '0 0 6px', letterSpacing: -0.3 }}>Career Hub</h2>
          <p style={{ fontSize: 14, color: 'var(--brand-slate)', margin: 0, lineHeight: 1.5, maxWidth: 520 }}>
            Not sure which IT Career is right for you or how to get into the role that you want.
            Take a look at a few of the roles below and add the ones you like to your Roadmap.
            Once you're done, head over to the Roadmap page to find out what certifications are
            necessary to get into the career you want.
          </p>
          <button type="button" onClick={() => onNavigate?.('roadmap')}
            style={{ ...btnAmber, marginTop: 14 }}>
            Go to Roadmap →
          </button>
          <p style={{ fontSize: 9.5, fontWeight: 600, color: '#9aa3b2', margin: '8px 0 0', letterSpacing: 0.2 }}>
            Powered by Adzuna · Updated today
          </p>
        </div>
        <div style={{ flex: '1.15 1 360px', minWidth: 0, maxWidth: 580 }}>
          <CareerPathMap track={track} compact />
        </div>
      </div>

      {/* Two columns */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* LEFT */}
        <div style={{ flex: '2.6 1 460px', minWidth: 0 }}>
          {/* Search */}
          <div style={{ ...cardWhite }}>
            <p style={sectionTitle}>Find your next role</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
              <select value={trackId} onChange={e => setTrackId(e.target.value)} style={{ ...field, flex: '1 1 180px', cursor: 'pointer' }}>
                {Object.entries(TRACKS).map(([id, t]) => <option key={id} value={id}>{t.label || id}</option>)}
              </select>
              <input placeholder="Job title or keyword" value={keyword} onChange={e => setKeyword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchJobs(trackId, keyword, location); }}
                style={{ ...field, flex: '2 1 200px' }} />
              <input placeholder="City or ZIP" value={location} onChange={e => setLocation(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchJobs(trackId, keyword, location); }}
                style={{ ...field, flex: '1 1 140px' }} />
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
              <select value={salaryMin} onChange={e => setSalaryMin(e.target.value)} style={{ ...field, flex: '0 0 auto', cursor: 'pointer' }}>
                {SALARY_BANDS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {WORK_TYPES.map(w => {
                  const active = workType === w.id;
                  return (
                    <button key={w.id} type="button" onClick={() => setWorkType(w.id)}
                      style={{
                        padding: '7px 14px', borderRadius: 99, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${active ? 'var(--brand-amber)' : '#e5e7eb'}`,
                        background: active ? '#fffaf0' : '#fff',
                        color: active ? 'var(--brand-amber)' : 'var(--brand-slate)',
                      }}>
                      {w.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button type="button" onClick={() => fetchJobs(trackId, keyword, location)}
              style={{ ...btnAmber, marginTop: 14, padding: '12px 24px', width: 'auto' }}>
              {loading ? 'Searching…' : 'Find Matching Roles →'}
            </button>
          </div>

          {/* Recommended Roles */}
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <p style={{ ...sectionTitle, margin: 0 }}>Recommended Roles for You</p>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: canAddRoadmap ? 'var(--brand-slate)' : 'var(--brand-amber)' }}>
                {perTrackCount}/{MAX_ROADMAP_PER_TRACK} added · {roadmapRoles.length}/{MAX_ROADMAP_TOTAL} on roadmap
              </span>
            </div>
            {loading && <p style={muted}>Loading live roles…</p>}
            {error && !loading && <p style={muted}>Couldn't load roles right now. Try again shortly.</p>}
            {!loading && !error && ranked.length === 0 && <p style={muted}>No roles match these filters. Widen your search.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 372, overflowY: 'auto', paddingRight: 6 }}>
              {ranked.slice(0, 12).map(({ job }) => (
                <RoleCard key={job.id} job={job} track={track}
                  inRoadmap={roadmapRoles.some(r => roleMatchesJob(r, job, trackId))}
                  canAdd={canAddRoadmap}
                  onToggleRoadmap={toggleRoadmap} />
              ))}
            </div>
          </div>

        </div>

        {/* RIGHT — stat cards */}
        <div style={{ flex: '1 1 210px', maxWidth: 258, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <StatCard icon={<IcTarget />} label="Career Goal" value={careerGoal}
            desc={`Your ${track?.label} target role`} cta="View goal plan" />
          <StatCard icon={<IcMatch />} label="Job Match" value={`${topMatch}% match`}
            desc="Top alignment among live listings" cta="See matching roles" />
          <StatCard icon={<IcTrend />} label="Market Signal" value={demand || 'Loading…'}
            desc={count != null ? `${count.toLocaleString()} live listings` : 'Live demand for this role'} cta="View market" />
          <StatCard icon={<IcDollar />} label="Salary Snapshot" value={salaryStat || '—'}
            desc="Typical range from current listings" cta="See salary data" />
        </div>
      </div>
    </div>
  );
}

// ── icons ──────────────────────────────────────────────────────
function IcTarget() { return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>); }
function IcMatch()  { return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>); }
function IcTrend()  { return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/></svg>); }
function IcDollar() { return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>); }

// ── shared styles ──────────────────────────────────────────────
const cardWhite = {
  background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14,
  padding: '18px 20px', boxShadow: 'var(--card-shadow)',
};
const sectionTitle = { fontSize: 15, fontWeight: 800, color: '#1e1e2e', margin: 0, letterSpacing: -0.2 };
const muted = { fontSize: 13, color: 'var(--brand-slate)', margin: '0 0 12px' };
const field = {
  padding: '10px 13px', borderRadius: 10, fontSize: 13,
  border: '1px solid #e5e7eb', background: '#f8fafc', color: '#1e1e2e', fontFamily: 'inherit', outline: 'none', minWidth: 0,
};
const btnAmber = {
  padding: '10px 18px', borderRadius: 10, background: 'var(--brand-amber)', border: 'none',
  color: '#ffffff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 4px 14px rgba(217,119,6,0.26)',
};
const roleBtn = {
  padding: '9px 14px', borderRadius: 9, fontSize: 13, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
};
const btnSave    = { ...roleBtn, background: '#fff', border: '1px solid #cbd2dc', color: 'var(--brand-slate)' };
const btnRoadmap = { ...roleBtn, background: 'var(--brand-amber)',   border: 'none', color: '#ffffff' };
const btnApply   = { ...roleBtn, background: 'var(--brand-emerald)', border: 'none', color: '#ffffff' };
